import { createInertiaApp } from '@inertiajs/react'
import { StrictMode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import AuthLayout from '@/layouts/auth-layout'
import { ConfirmDialogProvider } from '@/components/confirm-dialog-provider'
import { DrawerProvider } from '@/components/drawer-provider'
import '@/i18n'

createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: (name) => {
    const pages = import.meta.glob('../pages/**/*.jsx', {
      eager: true,
    })
    const pageModule = pages[`../pages/${name}.jsx`]
    const page = pageModule?.default

    if (!page) {
      console.error(`Missing Inertia page component: '${name}.jsx'`)
      return undefined
    }

    // Default application layout rendered in React.
    page.layout ||= (pageElement) => <AuthLayout>{pageElement}</AuthLayout>

    return page
  },

  setup({ el, App, props }) {
    const app = (
      <StrictMode>
        <ConfirmDialogProvider>
          <DrawerProvider>
            <App {...props} />
          </DrawerProvider>
        </ConfirmDialogProvider>
      </StrictMode>
    )

    // In CSR mode, #app usually only contains the Inertia JSON script tag.
    // Hydration is only valid when actual server-rendered element markup exists.
    const hasServerRenderedMarkup = Array.from(el.childNodes).some((node) => {
      if (node.nodeType === Node.TEXT_NODE) return node.textContent?.trim()
      if (node.nodeType !== Node.ELEMENT_NODE) return false
      return !(node.tagName === 'SCRIPT' && node.hasAttribute('data-inertia'))
    })

    if (hasServerRenderedMarkup) {
      hydrateRoot(el, app)
    } else {
      createRoot(el).render(app)
    }
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
    future: {
      useScriptElementForInitialPage: true,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
      "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
      'Consider moving <%= vite_javascript_tag "inertia.jsx" %> to the Inertia-specific layout instead.',
    )
  }
})
